import * as React from 'react'
import {useEffect, useState} from 'react'
import {Button, Col, DatePicker, Form, Radio, Row, Table, Typography} from 'antd';
import PublisherDoughnutChart from './PublisherDoughnutChart';
import {addCommas, beautifyReportData, DateOption, roundBySlice, useWindowSize, WindowSize} from '../../../../util/Util';
import moment from 'dayjs';
import {
    KeyFilterClause,
    PublisherReportKeyColumnDescription,
    PublisherReportValueColumnDescription,
} from '../../../../api/api';
import reportApi from '../../reports/ReportApi';
import PublisherPieChart from './PublisherPieChart';
import PublisherBarChart from './PublisherBarChart';
import './style.css'
import {ColumnsType} from 'antd/lib/table';

const {Text} = Typography;
const {RangePicker} = DatePicker;

const FormItem = Form.Item
const dateFormat = 'YYYY-MM-DD'
const PublisherKeyColumns = PublisherReportKeyColumnDescription.ColumnEnum
const PublisherValueColumns = PublisherReportValueColumnDescription.ColumnEnum
let pubDat: PublisherData[] = [];

export interface PublisherData {
    name: string;
    revenue: string;
    request: string;
    win: string;
    impression: string;
    view: string;
    click: string;
    vastStart: string;
    vastComplete: string;
}

export const PublisherDashboard = () => {
    const windowSize: WindowSize = useWindowSize();

    interface DataType {
        key: React.ReactNode;
        dsp: string;
        impressionsCount: string;
        winsCount: string;
        revenue: string;
        children?: DataType[];
    }

    const [selectedRow, setSelectedRow] = useState()
    const [revenueChart, setRevenueChart] = useState(true)
    const [eventsChart, setEventsChart] = useState(false)
    const [datePeriod, setDatePeriod] = useState<any>(DateOption.Month)
    const [dates, setDates] = useState<[moment.Dayjs, moment.Dayjs]>([moment(moment().subtract(1, 'months'), dateFormat), moment(moment(), dateFormat)])
    const [form] = Form.useForm()

    const [loading, setLoading] = useState(false)
    const [hideDiagram, setHideDiagram] = useState(false)
    const [tableData, setTableData] = useState<DataType[]>([])
    const [colSpan, setColSpan] = useState(window.innerWidth < 1850 ? 12 : 8);
    const columns: ColumnsType<DataType> = [
        {
            title: 'Dsp',
            dataIndex: 'dsp',
            width: '30%',
            key: 'dsp',
        },
        {
            title: 'Revenue',
            dataIndex: 'revenue',
            width: '30%',
            key: 'revenue',
        },
        {
            title: 'Wins',
            dataIndex: 'winsCount',
            width: '20%',
            key: 'winsCount',
        },
        {
            title: 'Imp',
            dataIndex: 'impressionsCount',
            key: 'impressionsCount',
            width: '20%',
        }
    ]

    function prepareAndSetData(rowsData?: string[][]) {
        let tData: DataType[] = []

        rowsData?.forEach((row, idx) => {
            if (row[0]) {
                tData.push({
                    key: idx,
                    dsp: row[0],
                    revenue: row[1],
                    winsCount: row[3],
                    impressionsCount: row[4]
                });
            }
        });

        setTableData(tData)
    }

    const loadData = async () => {
        setLoading(true)
        let dateCol: PublisherReportKeyColumnDescription
        dateCol = {
            column: PublisherKeyColumns.Date,
            where: {
                type: KeyFilterClause.TypeEnum.Between,
                value: dates?.map(d => d.format(dateFormat))
            }
        }
        dateCol.onlyFilter = true
        const keyCols: PublisherReportKeyColumnDescription[] = []
        keyCols.push({column: PublisherKeyColumns.Dsp})
        keyCols.push(dateCol)
        keyCols.push({
            column: PublisherKeyColumns.DspId,
            where: {
                type: KeyFilterClause.TypeEnum.NotEquals,
                value: 0
            },
            onlyFilter: true
        })
        const valueCols: PublisherReportValueColumnDescription[] = []
        valueCols.push({column: PublisherValueColumns.RevenueWin})
        valueCols.push({column: PublisherValueColumns.Requests})
        valueCols.push({column: PublisherValueColumns.Wins})
        valueCols.push({column: PublisherValueColumns.Impressions})
        valueCols.push({column: PublisherValueColumns.View})
        valueCols.push({column: PublisherValueColumns.Click})
        valueCols.push({column: PublisherValueColumns.VastStart})
        valueCols.push({column: PublisherValueColumns.VastComplete})

        const uuid = await reportApi.createPublisher({
            keyColumns: keyCols,
            valueColumns: valueCols
        }).then(resp => resp.data)
        if (!uuid) {
            return
        }
        let interval = setInterval(async () => {

            try {
                const response = await reportApi.get(uuid ?? '');
                clearInterval(interval);
                const blob = await response.blob();
                const text = await blob.text();
                if (text.length < 1) {
                    return;
                }
                const rows = text.split('\n');
                if (rows.length < 2) {
                    return;
                }
                let header = rows[0].split(',');
                rows.shift();
                rows.pop();
                let rowsData = rows.map(row => row.split(','));
                pubDat = []

                rowsData.map(d => pubDat.push({
                    name: d[0],
                    revenue: roundBySlice(d[1]),
                    request: d[2],
                    win: d[3],
                    impression: d[4],
                    view: d[5],
                    click: d[6],
                    vastStart: d[7],
                    vastComplete: d[8]
                }));
                beautifyReportData(header, rowsData);
                prepareAndSetData(rowsData);
                setLoading(false);
            } catch (response) {
                if (typeof response === 'object') {
                    if (response.status !== 425) {
                        setLoading(false);
                        clearInterval(interval);
                    }
                }
            }
        }, 500)
    }

    const initialLoad = () => {
        loadData()
    }

    useEffect(() => {
        initialLoad()
    }, [])

    useEffect(() => {
        if (windowSize.width && windowSize.width < 1850) {
            setHideDiagram(true);
            setColSpan(12)
        } else {
            setHideDiagram(false)
            setColSpan(8)
        }
    }, [windowSize])

    const handleDatePeriod = (e) => {
        setDatePeriod(e.target.value)
        setDates([moment(moment().subtract(1, e.target.value), dateFormat), moment(moment(), dateFormat)])
    }

    const handleDates = (e) => {
        setDatePeriod(undefined)
        setDates(e)
    }

    const rowSelect = (e) => {
        setSelectedRow(e ? e.dsp : undefined)
    }

    function selectChart(e) {
        setRevenueChart(false)
        setEventsChart(false)

        if (e.target.value === 'revenue') {
            setRevenueChart(true)
        } else {
            setEventsChart(true)
        }
    }

    return (
        <div>
            <Row wrap={false}>
                <Col span={colSpan}  hidden={hideDiagram && !revenueChart}>
                    <PublisherPieChart style={{
                        height: '415px'
                    }}
                                       pubDat={pubDat}
                    />
                    <Col>
                        <PublisherDoughnutChart style={{
                            height: '415px'
                        }}
                                                pubDat={pubDat}
                        />
                    </Col>
                </Col>
                <Col span={colSpan} hidden={hideDiagram && !eventsChart}>
                    <PublisherBarChart style={{
                        height: '850px'
                    }}
                                       pubDat={pubDat}
                                       dsp={selectedRow}
                    />
                </Col>
                <Col span={colSpan}>
                    <Form
                        style={{
                            maxWidth: '1000px',
                            alignContent: 'center'
                        }}
                        onFinish={loadData}
                        form={form}
                        labelCol={{span: 1}}
                        wrapperCol={{span: 21}}
                    >
                        <FormItem style={{margin: '0px 0px -10px 10px'}}>
                            <Radio.Group id={'radioButtonsWithoutLines'} value={datePeriod} onChange={handleDatePeriod}>
                                {Object.keys(DateOption).map(option => <Radio.Button
                                    style={{border: '0px'}}
                                    key={option}
                                    value={option}>{option}</Radio.Button>)}
                            </Radio.Group>
                        </FormItem>

                        <FormItem
                            rules={[
                                {
                                    required: true,
                                    message: 'Please choose dates'
                                }
                            ]}
                        >
                            <RangePicker value={dates} onChange={handleDates}
                                         style={{width: '280px', margin: '0 0 0 10px'}} format="YYYY-MM-DD"/>
                            <Button style={{
                                backgroundColor: '#657dcb', margin: '10px 10px 0 10px'
                            }} type="primary" htmlType="submit" loading={loading}>Apply</Button>
                        </FormItem>
                    </Form>
                    <Radio.Group defaultValue="revenue" size="small" style={{ margin: '0px 0px 5px 10px' }} onChange={selectChart}>
                        <Radio.Button value="revenue"><i className="topbar__dropdownMenuIcon icmn-stats-bars"/>Revenue</Radio.Button>
                        <Radio.Button value="events"><i className="topbar__dropdownMenuIcon icmn-stats-bars"/>Events</Radio.Button>
                    </Radio.Group>
                    <Table
                        size={'middle'}
                        loading={loading}
                        id={'dashboardTable'}
                        scroll={{y: 600}}
                        bordered={true}
                        pagination={false}
                        columns={columns}
                        onRow={(record, rowIndex) => {
                            return {
                                onClick: () => {
                                    rowSelect(record)
                                },
                            };
                        }}
                        dataSource={tableData}
                        summary={pageData => {
                            let totalRevenue = 0;
                            let totalWins = 0;
                            let totalImpressions = 0;

                            pageData.forEach(({revenue, winsCount, impressionsCount}) => {
                                totalRevenue += Number.parseFloat(revenue.replace(/[$\s,]/g, ''));
                                totalWins += Number.parseFloat(winsCount.replace(',', ''));
                                totalImpressions += Number.parseFloat(impressionsCount.replace(',', ''));
                            });

                            return (
                                <>
                                    <Table.Summary.Row style={{backgroundColor: 'lightgrey'}}>
                                        <Table.Summary.Cell index={0}>
                                            <a style={{display: 'flex'}}
                                               onClick={() => rowSelect(undefined)}>Total</a></Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                            <Text>{'$ ' + addCommas(totalRevenue.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2}>
                                            <Text>{addCommas(totalWins.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={3}>
                                            <Text>{addCommas(totalImpressions.toString())}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </>
                            );
                        }}
                    />
                </Col>
            </Row>
        </div>
    )
}
